import { trans } from '@lingoda/i18n';
import { Button, type ButtonProps, IconButton, Stack, styled, useResolution } from '@lingoda/ui';
import { ArrowLeftIcon } from '@lingoda/ui/icons/lingodaIcons';
import type { ReactNode } from 'react';

type SkipButtonProps = Omit<ButtonProps, 'children'> & { label?: ReactNode };

interface StickyFooterProps {
    onBack?: () => void;
    onContinue?: () => void;
    continueType?: 'button' | 'submit';
    continueLabel?: string;
    continueDisabled?: boolean;
    continueLoading?: boolean;
    skipProps?: SkipButtonProps;
}

export const StickyFooter = ({
    onBack,
    onContinue,
    continueType = 'button',
    continueLabel,
    continueDisabled,
    continueLoading,
    skipProps,
}: StickyFooterProps) => {
    const isPhone = useResolution('phone');
    const showContinue = !!onContinue || continueType === 'submit';

    if (!onBack && !showContinue) return null;
    if (!showContinue && !isPhone) return null;

    return (
        <FooterStyled>
            <Stack
                spacing={2.5}
                direction="row"
                flexGrow={1}
                justifyContent={showContinue ? 'center' : 'left'}
            >
                {isPhone && onBack && (
                    <IconButton type="button" onClick={onBack} color="primary">
                        <ArrowLeftIcon size={24} />
                    </IconButton>
                )}

                {showContinue && (
                    <Button
                        type={continueType}
                        variant="contained"
                        fullWidth
                        sx={{ maxWidth: '400px' }}
                        onClick={onContinue}
                        disabled={continueDisabled}
                        loading={continueLoading}
                    >
                        {continueLabel ?? trans('continue', {}, 'student-onboarding')}
                    </Button>
                )}
            </Stack>

            {skipProps?.onClick && (
                <SkipButtonStyled type="button" {...skipProps}>
                    {skipProps.label ?? trans('skip', {}, 'student-onboarding')}
                </SkipButtonStyled>
            )}
        </FooterStyled>
    );
};

const FooterStyled = styled(Stack)(({ theme }) => ({
    position: 'fixed',
    bottom: 0,
    left: 0,
    width: '100%',
    backgroundColor: theme.colors.background.default,
    boxShadow: theme.shadows[3],
    padding: theme.spacing(3),
    zIndex: 1,
    flexDirection: 'row',
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
    },
}));

const SkipButtonStyled = styled(Button, {
    shouldForwardProp: (prop) => prop !== 'label',
})<SkipButtonProps>(({ theme }) => ({
    [theme.breakpoints.up('sm')]: {
        position: 'absolute',
        right: '150px',
    },
}));

export default StickyFooter;
